import React, { ReactElement, useContext, useEffect, useRef, useState } from "react";
import makeStyles from '@mui/styles/makeStyles';
import { Handle, Position, XYPosition } from "react-flow-renderer";
import { Marshal } from "../../fstn/marshal";
import StateDetails from "./StateDetails";
import { connect } from "react-redux";
import { selectFocus, selectModeData, selectProject} from "../../redux/selectors/editor";
import { ActionFactory } from "../../fstn/actionFactory";
import { Transition } from "../../fstn/transition";
import { IGraphData, updateFocus, UpdateModeData, updateModeData} from "../../redux/slices/editor";
import { Network } from "../../fstn/network";
import { IDebugController } from "../../fstn/debugController";
import ActionLayout from "./node/ActionLayout";
import {selectProjects, selectTags} from "../../redux/selectors/firebase";
import { ProjectDetails } from "../../redux/types/firebase";
import ContextMenu from "./node/ContextMenu";
import ScriptLayout from "./node/ScriptLayout";
import { FirebaseContext } from "../firebase/FirebaseProvider";
import AddTagDialog from "../tags/AddTagDialog";
import {IStateMeta} from "../../fstn/state";
import {Tags} from "./StateTags";
import ChipTags from "./ChipTags";

const nodeWidth = 240;
const useStyles = makeStyles(theme => ({
    action: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontSize: "x-small"
    },
    actions: {
        color: theme.palette.primary.contrastText,
        flexGrow: 1,
        maxHeight: 60,
        overflowY: "scroll",
        paddingLeft: theme.spacing(1),
        backgroundColor: "Background",
        '&::-webkit-scrollbar': {
            width: '0.4em'
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.1)',
            borderRadius: '0.4em'
        }
    },
    buttons: {
        flexGrow: 1,
        padding: theme.spacing(1),
        display: 'flex',
        flexDirection: 'column'
    },
    content: {
        backgroundColor: 'white',
        borderColor: "transparent",
        borderRadius: 8,
        borderStyle: "solid",
        borderWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        pointerEvents: "auto",
        width: nodeWidth,
    },
    current: {
        borderWidth: 2,
        borderStyle: "solid",
        borderColor: "red"
    },
    focus: {
        borderWidth: 2,
        borderStyle: "dashed"
    },
    selected: {
        borderWidth: 2,
        borderStyle: "dashed",
        borderColor: "blue",
        backgroundColor: "rgba(175, 238, 238, 0.5)",
    },
    selectedActive: {
        borderWidth: 3,
        borderStyle: "solid",
        borderColor: "blue",
        backgroundColor: "rgba(175, 238, 238, 0.5)",
    },
    icon: {
        marginRight: theme.spacing(1)
    },
    jumpNode: {
        position: "relative",
        backgroundColor: "white",
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        height: 100,
        width: 100,
        "&:after": {
            content: "''",
            position: "absolute",
            left: 100,
            bottom: 0,
            width: 0,
            height: 0,
            backgroundColor: "lightgray",
            borderTop: "50px solid transparent",
            borderBottom: "50px solid transparent",
            borderLeft: "50px solid white",
        }
    },
    script: {
        flexGrow: 1,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        fontSize: 'x-small',
        overflow: 'hidden'
    },
    summary: {
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        fontSize: 'x-small',
        fontStyle: 'italic',
        overflow: 'hidden'
    },
    toolbar: {
        borderBottom: 'solid 1px lightgrey',
        justifyContent: "space-between",
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        minHeight: 24
    },
    title: {
        flexGrow: 1,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    transition: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontSize: "x-small"
    },
    trigger: {
        leftMargin: theme.spacing(1)
    },
}));

type Props = {
    data: {
        actions: Marshal[]
        script: string
        child?: string
        current: string
        debugController: IDebugController | undefined
        factory: ActionFactory
        focus: string
        goto: { label: string, destination: string }[]
        incoming: string[]
        initial: string
        label: string
        network: Network
        parent: string
        transitions: Transition[]
        meta: IStateMeta
        onDoubleClick: (id: string) => void
        onSetSelected: (id: string) => void
        onTransitionDelete: (source: string, target: string | undefined, trigger: string | undefined) => void
        onSetBounds: (id: string, rect: XYPosition) => void
        borderColor?: string
    }
    id: string
    draggable: boolean
    focus: string
    graphData: IGraphData
    project: string
    projects: { [key: string]: ProjectDetails }
    updateFocus: (focus: string) => void
    updateModeData: UpdateModeData
    quickViewState: string | null;
    tags: Tags
};
function StateNode({tags, data, focus, id, graphData, project, projects, quickViewState, updateFocus,
                       updateModeData }: Props)
{
    const classes = useStyles();
    const [showDetails, setShowDetails] = useState(false);
    const [showTitle, setShowTitle] = useState(false);
    const [pauseState, setPauseState] = useState(
        data.debugController && data.debugController.getPausedStates().includes(id));
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const containerEl = useRef<HTMLDivElement>(null);
    const { removeState, setStateMember } = useContext(FirebaseContext);

    const [addTagType, setAddTagType] = useState("");

    const handleAddTagClose = () => {
        setAddTagType("");
        handleCloseContextMenu();
    }
    const handleContextMenu = (event: React.MouseEvent<HTMLElement>) =>
    {
        event.preventDefault();
        setAnchorEl(event.currentTarget);
    }
    const handleCloseContextMenu = () =>
    {
        setAnchorEl(null);
    }
    const handleDoubleClick = () =>
    {
        if (data.child) {
            updateModeData({root: id})
            updateFocus(data.child);
        } else {
            const initialState = data.network.states.get(data.network.initial);
            
            if (initialState?.parent === id) {
                updateModeData({root: id})
                updateFocus(data.network.initial);
            } else {
                const child = Array.from(data.network.states.keys())
                    .find(state => data.network.states.get(state)!.parent === id);

                if (child) {
                    updateFocus(child);
                } else {
                    data.onDoubleClick(id);
                    updateFocus(id);
                }
            }
        }
    }
    const handleEditClick = () =>
    {
        setShowDetails(true);
        handleCloseContextMenu();
    };
    const handleDeleteClick = () =>
    {
        removeState(project, id).catch(console.error);
        if (focus === id)
        {
            updateFocus("");
        }
        if (graphData.selected.includes(id))
        {
            updateModeData({
                selected: graphData.selected.filter(selected => selected !== id)
            });
        }
        handleCloseContextMenu();
    };
    const handleDetailsClose = () =>
    {
        setShowDetails(false)
    };
    const handleClickGoto = (id: string) =>
    {
        const state = data.network.states.get(id);
        
        if (state) {
            setShowDetails(false);
            updateFocus(id);
            if (state.parent !== graphData.root) {
                // Goto node under different root
                updateModeData({
                    root: state.parent,
                    selected: [],
                    selectedActive: ""
                });
            }
        }
    }
    const handleUpdateMember = (state: string, member: string, value: any) =>
    {
        setStateMember(project, state, member, value).catch(console.error);
    };
    const handlePauseClick = () =>
    {
        if (pauseState)
        {
            setPauseState(false);
            data.debugController?.removePauseState(id);
        } else
        {
            setPauseState(true);
            data.debugController?.addPauseState(id);
        }
        handleCloseContextMenu();
    }

    const contentClasses = [classes.content];
    const deleteEnabled = data.actions.length === 0 && data.transitions.length === 0 && data.incoming.length === 0;

    if (data.current) contentClasses.push(classes.current);
    if (data.focus) contentClasses.push(classes.focus);
    if (graphData.selected.includes(id)) contentClasses.push(classes.selected);
    if (graphData.selectedActive === id) contentClasses.push(classes.selectedActive);
    if (data.meta.type === "jump") contentClasses.push(classes.jumpNode);

    const layouts: { [key: string]: () => ReactElement } = {
        script: () =>
            <ScriptLayout
                id={id} data={data} onContextMenu={handleContextMenu} setShowTitle={setShowTitle}
                onClickGoto={handleClickGoto} projectDetails={projects[project]} tags={tags}/>,
        actions: () =>
            <ActionLayout
                id={id} data={data} onContextMenu={handleContextMenu} onClickGoto={handleClickGoto}
                projectDetails={projects[project]} tags={tags}/>
    }

    useEffect(() =>
    {
        if (containerEl.current && containerEl.current.parentElement)
            containerEl.current.parentElement.style.zIndex = quickViewState === id ? "15" : "3";
    }, [quickViewState, id])

    useEffect(() =>
    {
        if (containerEl.current)
        {
            data.onSetBounds(id, { x: containerEl.current.clientWidth, y: containerEl.current.clientHeight });
        }
    }, [containerEl, data, id]);

    const selection = graphData.selected.includes(id) ? graphData.selected : [id];

    return (
        <div ref={containerEl} title={showTitle ? id : undefined} className={contentClasses.join(" ")} onDoubleClick={handleDoubleClick}>
            <ContextMenu
                id={id} anchorEl={anchorEl} debugEnabled={data.debugController !== undefined} selection={selection}
                pauseState={pauseState} deleteEnabled={deleteEnabled} onClose={handleCloseContextMenu}
                handleEditClick={handleEditClick} handleDeleteClick={handleDeleteClick}
                handlePauseClick={handlePauseClick} network={data.network} setAddTagType={setAddTagType} project={project}
            />
            {addTagType && <AddTagDialog type={addTagType} selection={selection} onClose={handleAddTagClose} />}
            {showDetails && <StateDetails data={data} id={id} factory={data.factory} project={project}
                onClose={handleDetailsClose} onUpdateMember={handleUpdateMember} />}
            <Handle type="target" position={Position.Left} />
            {layouts[graphData.graphLayout] && layouts[graphData.graphLayout]()}
            <ChipTags data={data} state={id}/>
        </div>
    );
}
const mapStateToProps = (store: never) => ({
    focus: selectFocus(store),
    graphData: selectModeData(store) as IGraphData,
    project: selectProject(store),
    projects: selectProjects(store),
    tags: selectTags(store) ?? {}
});

export default connect(mapStateToProps, {
    updateFocus,
    updateModeData
})(StateNode);
